import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../services/api';
import { brandVars, BRAND_VAR_NAMES, isHexColor } from '../utils/color';

// Theme = a preset (peach | turquoise) plus the organisation's optional brand
// colour on top. Which preset: the user's own choice, else the organisation's
// default, else turquoise. The brand colour applies under either preset and
// users can't change it.

const STORAGE_KEY = 'ja_theme_user';
// Cached organisation look, replayed before first paint by index.html. Keyed
// per host; /switch-tenant (single-host deploys) clears it.
export const ORG_CACHE_KEY = 'ja_theme_org';
const VALID_THEMES = ['peach', 'turquoise'];
const DEFAULT_THEME = 'turquoise';

const ThemeContext = createContext({ theme: DEFAULT_THEME, setTheme: () => {}, brandColor: '', previewBrand: () => {}, previewPreset: () => {}, refreshOrgTheme: () => {} });

const readJson = (key) => { try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; } };

export function ThemeProvider({ children }) {
    const [userTheme, setUserTheme] = useState(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        return VALID_THEMES.includes(stored) ? stored : null;
    });
    const [org, setOrg] = useState(() => readJson(ORG_CACHE_KEY) || { themeDefault: DEFAULT_THEME, brandColor: '' });
    const [preview, setPreview] = useState(null); // admin's unsaved brand colour, or null
    const [presetPreview, setPresetPreview] = useState(null); // admin's unsaved default preset, or null

    const theme = presetPreview || userTheme || (VALID_THEMES.includes(org.themeDefault) ? org.themeDefault : DEFAULT_THEME);
    const brandColor = preview !== null ? preview : org.brandColor;

    const refreshOrgTheme = useCallback(() => api.get('/auth/config')
        .then(res => {
            const next = {
                themeDefault: VALID_THEMES.includes(res.data?.themeDefault) ? res.data.themeDefault : DEFAULT_THEME,
                brandColor: isHexColor(res.data?.brandColor) ? res.data.brandColor : '',
            };
            setOrg(next);
            return next;
        })
        .catch(() => null), []); // public pages / no tenant: keep defaults

    useEffect(() => { refreshOrgTheme(); }, [refreshOrgTheme]);

    // Apply the preset and the brand colour, and cache both for the next first paint.
    useEffect(() => {
        const root = document.documentElement;
        root.setAttribute('data-theme', theme);
        BRAND_VAR_NAMES.forEach(n => root.style.removeProperty(n));
        const vars = brandVars(brandColor, theme);
        Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
        if (preview === null && presetPreview === null) {
            try {
                localStorage.setItem(ORG_CACHE_KEY, JSON.stringify({
                    ...org,
                    vars: org.brandColor ? { peach: brandVars(org.brandColor, 'peach'), turquoise: brandVars(org.brandColor, 'turquoise') } : null,
                }));
            } catch { /* storage unavailable */ }
        }
    }, [theme, brandColor, org, preview, presetPreview]);

    const setTheme = (next) => {
        if (!VALID_THEMES.includes(next)) return;
        localStorage.setItem(STORAGE_KEY, next);
        setUserTheme(next);
    };

    // Admin settings: show an unsaved brand colour live; null goes back to the saved one.
    const previewBrand = useCallback((hex) => setPreview(hex === null ? null : (isHexColor(hex) ? hex : '')), []);
    // Same for the default preset, without touching the admin's own preference.
    const previewPreset = useCallback((preset) => setPresetPreview(VALID_THEMES.includes(preset) ? preset : null), []);

    return (
        <ThemeContext.Provider value={{ theme, setTheme, brandColor, orgTheme: org, previewBrand, previewPreset, refreshOrgTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    return useContext(ThemeContext);
}
